import {
  ChangeEvent,
  MouseEvent,
  FocusEvent,
  MutableRefObject,
  useMemo,
} from 'react';
import { FaCalendarAlt } from '@meronex/icons/fa';
import { getDateFormat, getLabel, TDateFormat, uniqueId } from '@apia/util';
import { IconInput } from './IconInput';
import { Box, Close, getVariant } from '@apia/theme';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ApiaUtil } from '../../objects';
import CalModal from './CalModal';
import dayjs from 'dayjs';

const DEFAULT_LOCALE = window.LANG_CODE;
const DEFAULT_MASK_PLACEHOLDER = '_';

const dateFormat: TDateFormat = getDateFormat();

const getMaskForDateFormat = (): string => {
  if (dateFormat === 'YYYY/MM/DD') return '9999/99/99';
  return '99/99/9999';
};

export type TDateProps = {
  className?: string;
  disabled?: boolean;
  id?: string;
  readonly?: boolean;

  // Specific props
  onBlur?: (date: string | null, ev?: FocusEvent<HTMLElement>) => unknown;
  /**
   * If a Promise<false> is returned, the value change is not accepted
   * and a msgInvalidDate error message is emitted.
   */
  onChange?: (date: string | null) => void | Promise<string | null>;
  onError?: (error: string) => unknown;
  value?: string;

  inputRef?: MutableRefObject<any>;
  props?: React.HTMLAttributes<HTMLInputElement>;
};

class DateInputController {
  props: Pick<TDateProps, 'onBlur' | 'onChange' | 'onError' | 'id'> = {};
  state: Pick<TDateProps, 'disabled' | 'readonly' | 'value'> = {};
  _ref: HTMLElement | null = null;

  error = false;
  inputValue = '';
  value: Date | null = null;

  constructor() {
    this.props.id = uniqueId('date-input');

    makeAutoObservable(this);
  }

  clear() {
    if (!this.state.disabled) {
      this.setValueFromString('');
      this.focus();
    }
  }

  focus() {
    if (!this.state.disabled) {
      const input = this._ref?.querySelector<HTMLInputElement>('input');
      if (input) {
        input.focus();
        setTimeout(() => {
          window.requestAnimationFrame(() => {
            input.setSelectionRange(0, input.value.length);
          });
        }, 0);
      }
    }
  }

  ref(el: HTMLElement | null) {
    this._ref = el;
  }

  get calendarElementId() {
    return `DateModal${this.props.id}`;
  }

  private isInsidePicker(el?: HTMLElement) {
    return !!el?.closest(
      `#${CSS.escape(this.props.id ?? '')}, #${CSS.escape(this.calendarElementId)}`,
    );
  }

  onBlur(ev: FocusEvent<HTMLElement, Element>) {
    if (!this.state.disabled) {
      if (!this.isInsidePicker(ev.relatedTarget as HTMLElement)) {
        if (this.error) {
          this.inputValue = '';
          this.value = null;
          this.error = false;
        }

        this.props.onBlur?.(this.inputValue, ev);
      }
    }
  }

  onInputChange(ev: ChangeEvent<HTMLInputElement>) {
    if (!this.state.disabled) this.setValueFromString(ev.target.value);
  }

  private onCalendarSelect(value: Date) {
    this.setValue(value);
  }

  openCalendar(ev: MouseEvent<any, any>) {
    if (!this.state.disabled) {
      const tooltip = ApiaUtil.instance.tooltips.open({
        attachToElement() {
          return (ev.target as HTMLElement).closest<HTMLElement>('button')!;
        },
        attachToElementAnchorPoint: 'center',
        closeOnClick: false,
        closeOnClickOut: true,
        closeOnEscape: true,
        closeOnMouseLeaveTooltip: false,
        closeOnMouseLeaveAttachedElement: false,
        closeOnScrollOut: true,
        children: (
          <div id={this.calendarElementId}>
            <CalModal
              calValue={this.value || new Date()}
              locale={DEFAULT_LOCALE}
              handleClickDay={(date) => {
                tooltip.close();
                this.onCalendarSelect(date);
                this.focus();
              }}
            />
          </div>
        ),
        initialFocusGetter(el) {
          return el.querySelector(`.react-calendar__tile--active`);
        },
        minSize: { width: 350, height: 310 },
        onClose: () => {
          window.requestAnimationFrame(() => {
            if (!this.isInsidePicker(document.activeElement as HTMLElement)) {
              this.props.onBlur?.(this.inputValue);
            }
          });
        },
      });
    }
  }

  setValueFromString(str: string, fire = true) {
    if (str === '' || str.replaceAll('_', '9') === getMaskForDateFormat()) {
      this.inputValue = '';
      this.setValue(null, fire);
    } else if (str !== this.inputValue) {
      const hasPlaceHolder = str.match(/_/);
      if (!hasPlaceHolder) {
        const value = dayjs(str, dateFormat);
        const formattedDate = value.format(dateFormat);
        if (value.isValid() && formattedDate === str) {
          this.setValue(value.toDate(), fire);
          /**
           * TWO Situations where inputValue must be directly assigned!
           */
        } else {
          this.inputValue = str;
          this.props.onError?.(getLabel('msgInvalidDate').text);
          this.error = true;
        }
      } else {
        this.inputValue = str;
      }
    }
  }

  async setValue(date: Date | null, fire = true) {
    this.error = false;

    if (date !== this.value) {
      const dateValue = dayjs(date);
      const newValue = dateValue.isValid() ? dateValue.format(dateFormat) : '';
      const res = fire ? await this.props.onChange?.(newValue) : null;

      this.inputValue = newValue || '';
      if (typeof res !== 'string') {
        this.value = date;
      } else {
        this.error = true;
        this.value = null;
        this.props.onError?.(res);
      }
    }
  }

  previousValue: string | null = '';
  render(props: TDateProps) {
    const { onBlur, onChange, onError, id } = props;
    Object.assign(this.props, {
      onBlur,
      onChange,
      onError,
      id: id ?? this.props.id,
    });

    const { value, disabled, readonly } = props;
    Object.assign(this.state, { readonly, value, disabled });

    if (value !== undefined && this.previousValue !== value) {
      this.previousValue = value;
      this.setValueFromString(value, false);
    }
  }
}

export const DateInput = observer(
  ({
    className,
    disabled,
    id: outerId,
    inputRef,
    onBlur,
    onChange,
    onError,
    readonly,
    value: outerValue,
    props,
  }: TDateProps) => {
    const controller = useMemo(() => new DateInputController(), []);
    controller.render({
      className,
      disabled,
      id: outerId,
      inputRef,
      onBlur,
      onChange,
      onError,
      readonly,
      value: outerValue,
      props,
    });

    return (
      <Box
        {...getVariant('forms.dateInput')}
        className={`${className ?? ''} dateInput`}
        id={controller.props.id}
        onBlur={controller.onBlur.bind(controller)}
        ref={controller.ref.bind(controller)}
      >
        <IconInput
          additionalButtons={
            controller.inputValue &&
            getMaskForDateFormat().replaceAll('9', DEFAULT_MASK_PLACEHOLDER) !==
              controller.inputValue &&
            !disabled ? (
              <Close
                as="button"
                onClick={controller.clear.bind(controller)}
                className="delete_date_button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              />
            ) : undefined
          }
          buttonProps={{ disabled }}
          icon={FaCalendarAlt}
          inputProps={{
            disabled,
            onChange: controller.onInputChange.bind(controller),
            value: controller.inputValue,
            ...props,
          }}
          mask={getMaskForDateFormat()}
          maskPlaceholder={DEFAULT_MASK_PLACEHOLDER}
          onClick={controller.openCalendar.bind(controller)}
          readOnly={readonly}
        />
      </Box>
    );
  },
);
